import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useMatch, useNavigate } from "react-router-dom";
import { doc } from "firebase/firestore";
import AppBar from "@mui/material/AppBar";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { CircleUserRound, LogOut, Menu as MenuIcon, UserRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { db } from "../../lib/firebase";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import { SidebarContent } from "../Sidebar";
import type { Event } from "@event-planner/shared";

/** Width of the slide-in navigation drawer; never wider than 85% of the screen. */
const PHONE_DRAWER_WIDTH = "min(300px, 85vw)";

/**
 * Phone shell pieces (below 900 px). DashboardLayout swaps the desktop
 * Header and permanent Sidebar for these two, and keeps the page content in
 * the same place in the tree, so crossing 900 px (rotating an iPad, resizing
 * a window) never remounts the open page or drops unsaved form edits.
 *
 * The nav drawer slides in over the page and shows the same SidebarContent
 * as the desktop sidebar, so both shells share one nav definition. It
 * closes on every route change, which covers nav links, the event switcher,
 * and browser back.
 */
export function PhoneNavDrawer({
  activeEventId,
  open,
  onClose,
}: {
  /** Same meaning as the Sidebar prop: the event the nav links point into. */
  activeEventId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const location = useLocation();

  useEffect(() => {
    onClose();
    // Close only on navigation, not when the parent re-creates onClose.
  }, [location.pathname]);

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          width: PHONE_DRAWER_WIDTH,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <SidebarContent activeEventId={activeEventId} />
    </Drawer>
  );
}

/**
 * Full-width top bar: menu button, the open event's name (the event
 * switcher is one tap away in the drawer), and an account menu.
 */
export function PhoneTopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);

  // Name the event only while a page inside it is open; elsewhere the
  // portal name is the honest title.
  const match = useMatch("/events/:eventId/*");
  const routeEventId =
    match?.params.eventId && match.params.eventId !== "new"
      ? match.params.eventId
      : null;
  const eventRef = useMemo(
    () => (routeEventId ? doc(db, "events", routeEventId) : null),
    [routeEventId],
  );
  const { data: event } = useFirestoreDocOnce<Event>(eventRef);
  const title = routeEventId && event ? event.name : "Flat Rock Events";

  // Same order as the desktop Header: leave first so ProtectedRoute does not
  // preserve the current page for after the next sign-in.
  async function handleSignOut() {
    setAccountAnchor(null);
    navigate("/login", { replace: true });
    await signOut();
  }

  return (
    <AppBar position="fixed">
      <Toolbar sx={{ gap: 1 }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onOpenNav}
          aria-label="Open navigation"
          data-testid="phone-nav-open"
        >
          <MenuIcon size={22} />
        </IconButton>
        <Typography variant="h6" component="div" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {user && (
          <>
            <IconButton
              color="inherit"
              edge="end"
              onClick={(e) => setAccountAnchor(e.currentTarget)}
              aria-label="Account"
              aria-haspopup="menu"
              aria-expanded={Boolean(accountAnchor)}
            >
              <CircleUserRound size={22} />
            </IconButton>
            <Menu
              anchorEl={accountAnchor}
              open={Boolean(accountAnchor)}
              onClose={() => setAccountAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                component={RouterLink}
                to={`/users/${user.uid}`}
                onClick={() => setAccountAnchor(null)}
                aria-label="View your profile"
              >
                <ListItemIcon>
                  <UserRound size={18} />
                </ListItemIcon>
                <ListItemText
                  primary="Your profile"
                  secondary={user.email}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </MenuItem>
              <MenuItem onClick={handleSignOut}>
                <ListItemIcon>
                  <LogOut size={18} />
                </ListItemIcon>
                <ListItemText primary="Sign out" />
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
}
